import { default as IconPinClass } from "@/assets/IconPinClass.svg";
import { ClassroomEditV2 } from "@/js/components/ClassroomEditV2";
import useStore from "@/stores/index";
import { getProviderDisplayText } from "@/js/utils/providerUtils";
import { generateClassEndsStr_tile } from "@/js/utils/timeUtils";
import { useAccountSettingsUse24Hour } from "@/query/accountSettingsQuery";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import shallow from "zustand/shallow";
import iconMerge from "@/assets/IconMerge.svg?url";
import ClassroomRename from "../ClassroomRename";
import { TEACHER_ACTIONS } from "../Classrooms";
import { SettingsModal } from "../CombineClasses/SettingsModal";
import { CLASSROOM_SOURCE_TYPES, ClassroomSourceType, ClassroomType } from "types/classrooms";
import { Box, Tooltip, useDisclosure } from "@chakra-ui/react";
import { envConfig } from "@/config";
import { ModifyStudentsModal } from "@/components/ModifyStudentsModal";
import { ModifyTeachersModal } from "@/components/ModifyTeachersModal";
import ErrorBoundary from "@/components/ErrorBoundary";
import { getClassroomDisplayName } from "@/utils/classroomUtils";
import { DeleteClassConfirmModal } from "@/components/DeleteClassConfirmModal";
import { ClassroomTileButton } from "./ClassroomTileButton";
import { ClassroomStudentCount } from "./ClassroomStudentCount";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { SchedulingModal } from "@/components/SchedulingModal";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";

interface ClassroomTileV2Props {
	classroom: ClassroomOverviewDTO;
	isActive: boolean;
	onClick: (action: string, classroom: ClassroomOverviewDTO) => void;
	expiry?: number;
}

export const EDIT_CLASSROOM_ACTION = {
	save: "save",
	delete: "delete",
	cancel: "cancel",
	deleteConfirmed: "deleteConfirmed",
	saveComplete: "saveComplete",
};

const ClassroomTileV2 = (props: ClassroomTileV2Props): JSX.Element => {
	const { t } = useTranslation();
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [isActive, setIsActive] = useState(props.isActive);
	const [personalisation, setPersonalisation] = useState(props.classroom.personalisation);
	const [isRenameOpen, setRenameOpen] = useState(false);
	const maxStudents = envConfig.MAX_STUDENTS;

	const modifyStudentsModal = useDisclosure();
	const modifyTeachersModal = useDisclosure();
	const deleteClassroomConfirmationModal = useDisclosure();
	const schedulingModal = useDisclosure();

	const classwizeFeatures = getFeatures();
	const use24Hour = useAccountSettingsUse24Hour();
	const isClassSessionActiveV2 = useStore((state) => state.classroom.isClassSessionActiveV2);

	const classroomStore = useStore(
		(state) => ({
			// @ts-expect-error - Leaving as it was in the original code
			toggleClassroomPin: state.classroom.toggleClassroomPin,
		}),
		shallow
	);

	const [studentCount, setStudentCount] = useState(props.classroom.memberCount);

	useEffect(() => {
		setStudentCount(props.classroom.memberCount);
	}, [props.classroom]);

	// HACK: Setting and clearing interval every time a prop is changed.
	// This is not the best way of doing this but it needs to be in place to fix the scheduled classroom and current active classroom fix.
	// Ideal way would be to use event driven i.e. event-service to send message to teacher with the active classroom.
	useEffect(() => {
		setIsActive(isClassSessionActiveV2(props.classroom.id));
		const interval = setInterval(() => setIsActive(isClassSessionActiveV2(props.classroom.id)), 30000);
		return () => {
			clearInterval(interval);
		};
	}, [props, isClassSessionActiveV2]);

	useEffect(() => {
		setPersonalisation(props.classroom?.personalisation);
	}, [props.classroom.personalisation]);

	const handleDeleteClassroom = () => {
		props.onClick(TEACHER_ACTIONS.delete, props.classroom);
		setIsEditOpen(false); // Close the class settings modal if open (for if deleting a non-monitoring classroom)
		deleteClassroomConfirmationModal.onClose(); // Close the delete confirmation modal if open (for if deleting a monitoring classroom)
	};

	const handleOpenClassroom = () => {
		props.onClick(TEACHER_ACTIONS.open, props.classroom);
	};

	const handleEditAction = (action: string) => {
		switch (action) {
			case EDIT_CLASSROOM_ACTION.cancel:
				setIsEditOpen(false);
				break;
			case EDIT_CLASSROOM_ACTION.delete:
				handleDeleteClassroom();
				break;
			default:
				break;
		}
	};

	const handleRenameAction = (action: string) => {
		switch (action) {
			case EDIT_CLASSROOM_ACTION.cancel:
			case EDIT_CLASSROOM_ACTION.save:
			case EDIT_CLASSROOM_ACTION.saveComplete:
				setRenameOpen(false);
				break;
			default:
				break;
		}
	};

	const classroomTileFooterButtons = () => (
		<Box className="Classroom_headerRight" onClick={(event) => event.stopPropagation()}>
			{/* Scheduling is supported for all non-monitoring classes (including combined) when the schedules feature is enabled. */}
			{classwizeFeatures.includes("schedules") && !props.classroom.session?.isMonitoring && (
				<ClassroomTileButton
					icon="fa-calendar"
					iconSize={3}
					iconVariant="regular"
					label={t("overview.scheduling")}
					onClick={() => schedulingModal.onOpen()}
					dataTestId="classroom-tile-scheduling"
				/>
			)}

			{classwizeFeatures.includes("add_user") && (
				<ClassroomTileButton
					icon="fa-users"
					iconVariant="solid"
					iconSize={3.5}
					label={t("overview.modifyStudents")}
					onClick={() => modifyStudentsModal.onOpen()}
					dataTestId="classroom-tile-modify-students"
				/>
			)}

			<ClassroomTileButton
				icon="fa-pen-to-square"
				iconVariant="solid"
				iconSize={3}
				label={t("overview.renameClass")}
				onClick={() => setRenameOpen(true)}
				dataTestId="classroom-tile-rename-classroom"
			/>

			{props.classroom.session?.isMonitoring && (
				<ClassroomTileButton
					icon="fa-trash-can"
					iconVariant="solid"
					iconSize={2.5}
					label={t("overview.deleteClass")}
					onClick={() => deleteClassroomConfirmationModal.onOpen()}
					dataTestId="classroom-tile-delete-classroom"
				/>
			)}
			{!props.classroom.session?.isMonitoring && (
				<ClassroomTileButton
					icon="fa-gear"
					iconVariant="solid"
					iconSize={3}
					label={t("overview.classroomSettings")}
					onClick={() => {
						if (props.classroom.sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.COMBINED) {
							setIsEditOpen(true);
						} else {
							modifyTeachersModal.onOpen();
						}
					}}
					dataTestId="classroom-tile-classroom-settings"
				/>
			)}
		</Box>
	);

	const displayName = useMemo(() => getClassroomDisplayName(props.classroom), [props.classroom]);

	const renderClassroomHeader = () => (
		<div className="Classroom_header">
			<span className="Classroom_title" title={displayName}>
				{displayName}
			</span>
		</div>
	);

	const renderProvider = () => {
		const sourceType = props.classroom.sourceType.toLowerCase() as ClassroomSourceType;
		const classType = props.classroom.type.toLowerCase() as ClassroomType;
		const providerDisplayText = getProviderDisplayText(sourceType, classType);
		return (
			<span className="Classroom_provider">
				{sourceType === CLASSROOM_SOURCE_TYPES.COMBINED && <img src={iconMerge} className="combined_icon" alt={t("overview.combined")} />}
				{providerDisplayText}
			</span>
		);
	};

	return (
		props.classroom && (
			<ErrorBoundary>
				<Box position={"relative"} className="Classroom">
					<div className="Card-inner">
						<Box as="button" onClick={handleOpenClassroom} className="Classroom-click" aria-label={displayName}></Box>
						{renderClassroomHeader()}
						<div className="Classroom_content">
							<ClassroomStudentCount memberCount={studentCount} maxStudents={maxStudents} />
							{renderProvider()}
						</div>
						{isEditOpen && props.classroom.sourceType.toLowerCase() === CLASSROOM_SOURCE_TYPES.COMBINED && (
							<SettingsModal open={isEditOpen} onClose={() => setIsEditOpen(false)} classroom={props.classroom} />
						)}
						{isEditOpen && props.classroom.sourceType.toLowerCase() !== CLASSROOM_SOURCE_TYPES.COMBINED && (
							<ClassroomEditV2 open={isEditOpen} handleAction={handleEditAction} classroom={props.classroom} />
						)}
						<ModifyTeachersModal
							isOpen={modifyTeachersModal.isOpen}
							onClose={modifyTeachersModal.onClose}
							classroom={props.classroom}
							onDeleteClass={() => {
								modifyTeachersModal.onClose();
								deleteClassroomConfirmationModal.onOpen();
							}}
						/>
						{isRenameOpen && <ClassroomRename open={isRenameOpen} handleAction={handleRenameAction} classroom={props.classroom} />}
						<ModifyStudentsModal
							isOpen={modifyStudentsModal.isOpen}
							onClose={modifyStudentsModal.onClose}
							classroom={props.classroom}
							isMonitoringClass={!!props.classroom.session?.isMonitoring}
							studentsModifiedCallback={(addedStudents, removedStudents) => {
								setStudentCount(studentCount + addedStudents.length - removedStudents.length);
							}}
						/>
						<DeleteClassConfirmModal
							open={deleteClassroomConfirmationModal.isOpen}
							classroomName={displayName}
							onDelete={handleDeleteClassroom}
							onCancel={deleteClassroomConfirmationModal.onClose}
						/>
						<SchedulingModal
							isOpen={schedulingModal.isOpen}
							onClose={schedulingModal.onClose}
							classroom={{
								...props.classroom,
								memberCount: studentCount,
							}}
						/>
						<Tooltip
							label={<span>{personalisation?.pinned ? t("overview.unpinClass") : t("overview.pinClass")}</span>}
							placement="top"
							hasArrow
						>
							<Box
								as="button"
								className="Pin_button"
								aria-label={personalisation?.pinned ? t("overview.unpinClassAria") : t("overview.pinClassAria")}
								onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
									event.stopPropagation();
									classroomStore.toggleClassroomPin(props.classroom.id);
								}}
							>
								{/* @ts-expect-error - Leaving as it was in the original code */}
								<IconPinClass className={personalisation?.pinned ? "Pin_active" : "Pin"} />
							</Box>
						</Tooltip>
						<div className="Classroom_tile_bottom">
							{isActive && props.expiry ? (
								<span className={props.classroom.session?.isMonitoring === true ? "Monitoring_pill" : "classEnds_pill"}>
									{generateClassEndsStr_tile(props.expiry, t, use24Hour)}
								</span>
							) : (
								props.classroom.session?.isMonitoring === true && <div className="Monitoring_pill">{t("overview.monitoringClass")}</div>
							)}
							{classroomTileFooterButtons()}
						</div>
					</div>
				</Box>
			</ErrorBoundary>
		)
	);
};

export default ClassroomTileV2;
